/**
 * Skeleton, lightweight loading placeholders shaped like the content they
 * replace. Each placeholder is Foundation's `Skeleton` (PLAT-279), sized with
 * its `width`, `height` and `radius` props, so the look is Foundation's grey
 * pulse. Only `SkeletonTable` still uses an app.css class (`.skeleton-row`),
 * because it styles real table cells.
 *
 * Pair with `useDelayedFlag` (200ms by default) so fast loads never flash
 * the skeleton in-and-out. Per Ean 2026-06-02 "no constant teasing loads."
 *
 * Three variants cover the page shapes the app actually uses today:
 *
 *   - <SkeletonBlock />            single rectangular block (e.g., a card body)
 *   - <SkeletonLines count={n} />  n stacked text rows (e.g., a description)
 *   - <SkeletonCardGrid />         repeating tile-grid for catalog-like pages
 *   - <SkeletonDetail />           record-detail header + summary + body
 *
 * Don't reinvent — extend with new variants when an existing page needs a
 * shape these don't cover.
 */

import { Skeleton } from '@olytic/foundation';

/** The row radius of the retired `.skeleton` class, kept so shapes don't change. */
const ROW_RADIUS = 4;

interface SkeletonBlockProps {
  /** Pixel height; defaults to 40 (the old `.skeleton-block` default). */
  height?: number | string;
  /** Pixel/percent width; defaults to '100%'. */
  width?: number | string;
  /** Border radius override; defaults to 8px. */
  radius?: number | string;
  /** Extra margin top. */
  marginTop?: number | string;
}

export function SkeletonBlock({
  height = 40,
  width = '100%',
  radius = 8,
  marginTop,
}: SkeletonBlockProps) {
  return (
    <Skeleton
      width={width}
      height={height}
      radius={radius}
      style={marginTop !== undefined ? { marginTop } : {}}
    />
  );
}

interface SkeletonLinesProps {
  count?: number;
  /** Optional varying widths so the lines don't read as a perfect grid. */
  widths?: Array<string | number>;
  marginTop?: number | string;
}

export function SkeletonLines({ count = 3, widths, marginTop }: SkeletonLinesProps) {
  const defaultWidths = ['92%', '78%', '85%', '60%'];
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        ...(marginTop !== undefined ? { marginTop } : {}),
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          width={widths?.[i] ?? defaultWidths[i % defaultWidths.length] ?? '100%'}
          height={12}
          radius={ROW_RADIUS}
        />
      ))}
    </div>
  );
}

interface SkeletonCardGridProps {
  /** Number of placeholder cards; defaults to 6. */
  count?: number;
  /** Pixel height per card; defaults to 140. */
  cardHeight?: number;
}

export function SkeletonCardGrid({ count = 6, cardHeight = 140 }: SkeletonCardGridProps) {
  return (
    <div className="tile-grid" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="tile"
          style={{
            height: cardHeight,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Skeleton width={32} height={32} radius={8} />
            <Skeleton width="50%" height={14} radius={ROW_RADIUS} />
          </div>
          <SkeletonLines count={2} widths={['90%', '60%']} />
          <Skeleton width={80} height={18} radius={6} style={{ marginTop: 'auto' }} />
        </div>
      ))}
    </div>
  );
}

/**
 * SkeletonTable — table-body skeleton. Push 5-F (2026-07-11) extracted from
 * three near-identical local defs (People.tsx, MyActivity.tsx,
 * activity/ActivityDispatches.tsx) so every table-loading surface renders the
 * same shape. Uses the `.skeleton-row` CSS class (app.css) which is purpose-
 * built for `<tr><td><div/></td></tr>` structure — SkeletonBlock would break
 * the row layout.
 */
interface SkeletonTableProps {
  rows: number;
  cols: number;
}

export function SkeletonTable({ rows, cols }: SkeletonTableProps) {
  return (
    <div className="table-wrap">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} className="skeleton-row">
              {Array.from({ length: cols }).map((__, c) => (
                <td key={c}>
                  <div style={{ width: c === 0 ? '70%' : '40%' }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Detail-page skeleton — header strip + summary + a couple of content blocks.
 * Matches the Lightning record-page shape (highlight bar → details card →
 * related lists) so the skeleton reads as the page becoming.
 */
export function SkeletonDetail() {
  return (
    <div aria-hidden="true">
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <Skeleton width={48} height={48} radius={12} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton width={220} height={20} radius={ROW_RADIUS} />
            <Skeleton width={320} height={12} radius={ROW_RADIUS} />
          </div>
        </div>
      </div>
      <div className="card card-padded" style={{ marginBottom: 'var(--card-gap)' }}>
        <SkeletonLines count={3} widths={['85%', '70%', '60%']} />
      </div>
      <div className="card card-padded">
        <SkeletonBlock height={140} />
      </div>
    </div>
  );
}
